import React, {
  useState,
  useEffect,
  forwardRef,
  useRef,
  ChangeEvent,
  Children,
  cloneElement,
  useMemo,
} from 'react';
import { Auth, CognitoUser } from '@aws-amplify/auth'; // eslint-disable-line
import { isEmpty, Hub, HubCapsule } from '@aws-amplify/core'; // eslint-disable-line
import { AUTH_STATES } from '../../constants/authConsts';
import {
  IAuthenticatorProps,
  IAuthenticatorState,
  IInputsState,
} from './common/types';

const AUTHENTICATOR_AUTHSTATE = 'amplify-authenticator-authState';
export const Authenticator = forwardRef<HTMLDivElement, IAuthenticatorProps>(
  (props, ref) => {
    const { authState, onStateChange, hideDefault, children } = props;

    const mounted = useRef(false);

    const [intitalAuthState] = useState(authState || 'signIn');
    const [authenticatorState, setAuthenticatorState] = useState<
      IAuthenticatorState
    >({
      authState: 'loading',
    });
    // state to store forms input values
    const [inputs, setInputs] = useState<IInputsState>({});

    // just checking if we have onStateChange function
    const changeState = (state: string, data?: CognitoUser) => {
      if (onStateChange) {
        onStateChange(state, data);
      }
    };

    // state is form type and data is auth data
    const handleStateChange = (state: string, data?: any) => {
      let tempState = state;
      if (tempState === authenticatorState.authState) {
        return;
      }

      // if user is logging out, next form will be signIn
      if (tempState === 'signedOut') {
        tempState = 'signIn';
      }

      localStorage.setItem(AUTHENTICATOR_AUTHSTATE, tempState);

      if (mounted.current) {
        setAuthenticatorState({
          authState: tempState,
          authData: data,
        });
      }
      changeState(tempState, data);
    };

    // check if the user is authenticated
    const checkUser = async () => {
      if (!mounted.current) {
        return;
      }

      try {
        const user = await Auth.currentAuthenticatedUser();
        // if user is authenticated, set the state to signedIn
        if (user) handleStateChange('signedIn', user);
      } catch (error) {
        // if user is not authenticated,
        // and form type in localstorage is signedIn then logged out the user
        // and set the state to signIn
        let cachedAuthState = null;
        cachedAuthState = localStorage.getItem(AUTHENTICATOR_AUTHSTATE);
        const promise =
          cachedAuthState === 'signedIn' ? Auth.signOut() : Promise.resolve();
        promise
          .then(() => handleStateChange(intitalAuthState))
          .catch(() => {
            throw new Error('Failed to sign out');
          });
      }
    };

    // checks if the user has verified their contact info
    const checkContact = (user: CognitoUser) => {
      let tempUser = user;
      Auth.verifiedContact(tempUser).then(data => {
        // data.verified consists of the verified contact info
        if (!isEmpty(data.verified)) {
          changeState('signedIn', user);
        } else {
          tempUser = Object.assign(user, data);
          changeState('verifyContact', user);
        }
      });
    };

    const onHubCapsule = (capsule: HubCapsule) => {
      const { channel, payload } = capsule;
      if (channel === 'auth') {
        switch (payload.event) {
          case 'cognitoHostedUI':
          case 'signIn':
            checkContact(payload.data);
            break;
          case 'cognitoHostedUI_failure':
            handleStateChange('signIn', null);
            break;
          case 'parsingUrl_failure':
            handleStateChange('signIn', null);
            break;
          case 'signOut':
            handleStateChange('signIn', null);
            break;
          case 'customGreetingSignOut':
            handleStateChange('signIn', null);
            break;
          default:
            break;
        }
      }
    };

    // on initial load checking in session if user is alreaday sign in
    useEffect(() => {
      mounted.current = true;
      checkUser();
      Hub.listen('auth', onHubCapsule);
      return () => {
        mounted.current = false;
        Hub.remove('auth', onHubCapsule);
      };
    }, []);

    const hide = useMemo(() => {
      if (!hideDefault) return [];
      return [AUTH_STATES.CONFIRM_SIGN_UP];
    }, [hideDefault]);

    // using name of the input as key saving the value in inputs state
    const handleInputChange = (evt: ChangeEvent<HTMLInputElement>) => {
      const { name, value, type, checked } = evt.target;
      const check_type = ['radio', 'checkbox'].includes(type);
      setInputs({ ...inputs, [name]: check_type ? checked : value });
    };

    let props_children: any[] = [];
    // just checking if we have childrens to render
    if (typeof children === 'object')
      if (Array.isArray(children)) props_children = children;

    // passing down the props to the all child components
    const render_props_children = Children.map(props_children, (child, index) =>
      cloneElement(child, {
        key: `aws-amplify-authenticator-props-children-${index}`, // eslint-disable-line
        authState: authenticatorState.authState,
        authData: authenticatorState.authData,
        onStateChange: handleStateChange,
        handleInputChange,
        changeState,
        checkContact,
        checkUser,
        inputs,
        hide,
      }),
    );

    return <div ref={ref}>{render_props_children}</div>;
  },
);

export default Authenticator; // eslint-disable-line
